import React, {
  useCallback,
  useState,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import format from 'string-template';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import {
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';

import AnnouncementContext from '../../../../../../context/AnnouncementContext';
import LoggedInUserContext from '../../../../../../../context/LoggedInUserContext';
import Announcement, { SCHEDULED_STATUS } from '../../../../../../Model/Announcement';
import ConfirmDialog from '../../../../../../components/ConfirmDialog';
import useToast from '../../../../../../hooks/useToast';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import { getCommunityLabel } from '../../../../../../utils/announcement';
import AnnouncementModal from '../../../AnnouncementModal';

import {
  TrashIcon,
  EditIcon,
  PinIcon,
  UnpinIcon,
  HideIcon,
  DeleteText,
} from './styles';
import texts from './texts.json';

// Shared shape behind both the delete and hide confirm dialogs: run the action, toast success,
// then close the dialog only if the component is still mounted. On failure, toast an error and
// leave the dialog open so the coach can see something went wrong and retry or cancel.
const useConfirmedAction = ({
  action, successText, showToast, isComponentMountedRef,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const confirm = useCallback(async () => {
    try {
      await action();
      showToast(successText);
      if (isComponentMountedRef.current) {
        setIsOpen(false);
      }
    } catch (error) {
      Sentry.captureException(error);
      if (isComponentMountedRef.current) {
        showToast(texts.actionError, { error: true });
      }
    }
  }, [action, successText, showToast, isComponentMountedRef]);
  return [isOpen, setIsOpen, confirm];
};

const PostOptionsMenu = ({
  announcement, onUnpinClick, coachOnly, isAggregated,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false);

  const { userId: loggedInUserId } = useContext(LoggedInUserContext);
  const {
    updateCommentAndAnnouncementState,
    handlePinRequest,
    communities,
  } = useContext(AnnouncementContext);

  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const {
    isPinned,
    scheduledStatus,
    isHidden,
    groupCommunityIds,
    communityId,
    communityName,
  } = announcement;
  const isScheduled = scheduledStatus === SCHEDULED_STATUS.PENDING;
  // Deleting a multi-community post takes it out of every community it went to. Dropping a single
  // community is done through Edit instead, by deselecting it.
  const groupCommunityCount = groupCommunityIds.length;

  const removeAction = useCallback(async () => {
    await Announcement.removeGroup(announcement, loggedInUserId);
    updateCommentAndAnnouncementState(null, null);
  }, [announcement, loggedInUserId, updateCommentAndAnnouncementState]);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen, removeAnnouncement] = useConfirmedAction({
    action: removeAction,
    successText: texts.success,
    showToast,
    isComponentMountedRef,
  });

  const hideAction = useCallback(() => announcement.hide(loggedInUserId), [announcement, loggedInUserId]);

  const [isHideDialogOpen, setIsHideDialogOpen, hideAnnouncement] = useConfirmedAction({
    action: hideAction,
    successText: texts.hideSuccess,
    showToast,
    isComponentMountedRef,
  });

  const handleMenuOpen = useCallback((e) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => setMenuAnchorEl(null), []);

  const handleEditClick = useCallback(() => {
    handleMenuClose();
    setIsAnnouncementModalOpen(true);
  }, [handleMenuClose]);

  const handleDeleteClick = useCallback(() => {
    handleMenuClose();
    setIsDeleteDialogOpen(true);
  }, [handleMenuClose, setIsDeleteDialogOpen]);

  const handleHideClick = useCallback(() => {
    handleMenuClose();
    setIsHideDialogOpen(true);
  }, [handleMenuClose, setIsHideDialogOpen]);

  const handlePinClick = useCallback(() => {
    handleMenuClose();
    handlePinRequest(announcement);
  }, [handleMenuClose, handlePinRequest, announcement]);

  const handleUnpinMenuClick = useCallback(() => {
    handleMenuClose();
    onUnpinClick();
  }, [handleMenuClose, onUnpinClick]);

  return (
    <>
      <IconButton size="small" onClick={handleMenuOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {coachOnly && (
          <>
            {!isScheduled && (
              isAggregated ? (
                // Pinning is per community, and this card stands for several, so there is no single
                // doc to pin. Kept visible but disabled, pointing at the post view - it renders the
                // selected community's copy, so Pin there acts on one doc.
                <MenuItem disabled>
                  <PinIcon />
                  {texts.menu.pinAggregated}
                </MenuItem>
              ) : (
                <MenuItem onClick={isPinned ? handleUnpinMenuClick : handlePinClick}>
                  {isPinned ? <UnpinIcon /> : <PinIcon />}
                  {isPinned ? texts.menu.unpin : texts.menu.pin}
                </MenuItem>
              )
            )}
            <MenuItem onClick={handleEditClick}>
              <EditIcon />
              {texts.menu.edit}
            </MenuItem>
            <Divider />
          </>
        )}
        {isAggregated ? (
          // Hiding is per community, and this card stands for several. Kept visible but disabled,
          // pointing at the post view, which acts on one copy - except on a scheduled post, which
          // has no comment icon and so no way into that view, where it is dropped entirely.
          !isScheduled && (
            <MenuItem disabled>
              <HideIcon />
              {texts.menu.hideAggregated}
            </MenuItem>
          )
        ) : (!isHidden && (
          <MenuItem onClick={handleHideClick}>
            <HideIcon />
            {texts.menu.hide}
          </MenuItem>
        ))}
        <MenuItem onClick={handleDeleteClick}>
          <TrashIcon />
          <DeleteText>{texts.menu.delete}</DeleteText>
        </MenuItem>
      </Menu>
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onCancel={() => setIsDeleteDialogOpen(false)}
        onConfirm={removeAnnouncement}
        dialogTexts={{
          title: texts.removeAnnouncement,
          content: groupCommunityCount > 1
            ? format(texts.removeGroupMessage, { count: groupCommunityCount })
            : texts.removeAnnouncementMessage,
        }}
      />
      <ConfirmDialog
        isOpen={isHideDialogOpen}
        onCancel={() => setIsHideDialogOpen(false)}
        onConfirm={hideAnnouncement}
        dialogTexts={{
          title: texts.hideAnnouncement,
          content: format(texts.hideAnnouncementMessage, {
            community: getCommunityLabel(communityId, communities, communityName),
          }),
        }}
      />
      {isAnnouncementModalOpen && (
        <AnnouncementModal
          isOpen={isAnnouncementModalOpen}
          onDidDismiss={() => setIsAnnouncementModalOpen(false)}
          announcement={announcement}
        />
      )}
    </>
  );
};

PostOptionsMenu.propTypes = {
  announcement: PropTypes.instanceOf(Announcement).isRequired,
  onUnpinClick: PropTypes.func,
  coachOnly: PropTypes.bool,
  isAggregated: PropTypes.bool,
};

PostOptionsMenu.defaultProps = {
  onUnpinClick: () => {},
  coachOnly: false,
  isAggregated: false,
};

export default compose(
  observer,
)(PostOptionsMenu);
